// Редът „Общо" на решетката „Доставка": сумата на ДДС, Нето и Бруто — всяка
// под своята колона, лепнат за дъното на картата (`WorksheetTotalsRow`).
// Нето и Бруто се пишат („Коригирай общото"): въведената сума от документа
// → разликата отива в реда с най-голяма сума (`useDeliveryTotalAdjust`).
// Enter / излизане прилага, Esc връща показаното.
import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  TotalsBlankCell,
  TotalsLabelCell,
  TotalsValueCell,
  WorksheetTotalsRow,
} from "../../components/worksheet/WorksheetTotalsRow";
import type { TotalColumn } from "./deliveryTotalAdjust";
import {
  FLAT_CELL_CLASS,
  LINE_GRID_CLASS,
  LINE_GRID_COLUMN_COUNT,
  formatPrice,
  parseDecimal,
} from "./deliveryWorksheetShared";

type Props = {
  count: number;
  totalNet: number;
  totalGross: number;
  vatApplies: boolean;
  // въведено общо → разликата в реда с най-голяма сума
  onAdjustTotal: (column: TotalColumn, target: number, current: number) => void;
};

export const DeliveryTotalsRow = ({ count, totalNet, totalGross, vatApplies, onAdjustTotal }: Props) => {
  const { t } = useTranslation();
  return (
    <WorksheetTotalsRow gridClass={LINE_GRID_CLASS} columnCount={LINE_GRID_COLUMN_COUNT}>
      {/* # · Продукт · Разфасовка · Количество · Ед. цена */}
      <TotalsLabelCell
        span={5}
        title={t("inventoryPage.total")}
        detail={t("inventoryPage.homeDraftLines", { count })}
      />
      <TotalsValueCell value={vatApplies ? formatPrice(totalGross - totalNet) : "—"} />
      <TotalsSumInput
        value={totalNet}
        label={t("inventoryPage.tadjNetLabel")}
        onCommit={(target) => onAdjustTotal("net", target, totalNet)}
      />
      <TotalsSumInput
        value={totalGross}
        strong
        label={t("inventoryPage.tadjGrossLabel")}
        onCommit={(target) => onAdjustTotal("gross", target, totalGross)}
      />
      {/* Партида · Срок · Склад · действия */}
      <TotalsBlankCell span={4} last />
    </WorksheetTotalsRow>
  );
};

// Сумата като поле: показва общото; при фокус се маркира цялото, въведеното
// се прилага при Enter/излизане, само ако е друго число
const TotalsSumInput = ({
  value,
  strong,
  label,
  onCommit,
}: {
  value: number;
  strong?: boolean;
  label: string;
  onCommit: (target: number) => void;
}) => {
  const shown = formatPrice(value);
  // null — не се редактира (показва се живото общо)
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft == null) return;
    setDraft(null);
    const target = parseDecimal(draft);
    if (draft.trim() === "" || target <= 0 || formatPrice(target) === shown) return;
    onCommit(target);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === "Escape") {
      // Esc тук е „откажи" — не връщане в търсачката / излизане от листа
      event.preventDefault();
      event.stopPropagation();
      setDraft(null);
      requestAnimationFrame(() => (event.target as HTMLInputElement).blur());
    }
  };
  return (
    <div className="flex min-w-0 items-center border-r border-shade-2 p-[2px]">
      <input
        aria-label={label}
        title={label}
        inputMode="decimal"
        value={draft ?? shown}
        onFocus={(event) => {
          setDraft(shown);
          const input = event.target;
          requestAnimationFrame(() => input.select());
        }}
        onChange={(event) => setDraft(event.target.value.replace(/[^0-9.,]/g, ""))}
        onBlur={commit}
        onKeyDown={onKeyDown}
        className={`${FLAT_CELL_CLASS} h-[30px] cursor-text px-[9px] py-0 text-right font-sheet tabular-nums hover:border-shade-3 ${
          strong ? "text-[15px] font-bold text-brand-deep-ink" : "text-[13.5px] font-semibold text-ink"
        }`}
      />
    </div>
  );
};

export default DeliveryTotalsRow;
